import { useState, useEffect, useRef, useCallback } from 'react';
import api, { getErrorMessage } from '../utils/api';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { MagnifyingGlass, CheckCircle, QrCode, X, Clock, Users, Bed, Camera, Robot } from '@phosphor-icons/react';
import { Html5Qrcode } from 'html5-qrcode';
import AIGuideDrawer from '../components/AIGuideDrawer';
import { imgUrl } from '../utils/imgUrl';
import { rankCustomersByFace } from '../utils/faceMatch';

const CheckIn = () => {
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('phone');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [todayCheckins, setTodayCheckins] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [currentBatchData, setCurrentBatchData] = useState({ regular: null, premium: null });
  const [nextBatchData, setNextBatchData] = useState({ regular: null, premium: null });
  const [waitingData, setWaitingData] = useState({ regular: [], premium: [] });
  const [graceStatus, setGraceStatus] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastToken, setLastToken] = useState(null);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [selectedBatchType, setSelectedBatchType] = useState(null); // 'regular' or 'premium'
  const [showBatchSelector, setShowBatchSelector] = useState(false);
  const [batchSelectorType, setBatchSelectorType] = useState(null); // Which selector to show
  const [nextBatchWaitingList, setNextBatchWaitingList] = useState([]);
  
  // Face ID states
  const [showFaceCapture, setShowFaceCapture] = useState(false);
  const [showCustomerSelection, setShowCustomerSelection] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const [faceSearchFilter, setFaceSearchFilter] = useState('');
  const [faceMatches, setFaceMatches] = useState([]);
  const [faceMatching, setFaceMatching] = useState(false);
  const [faceMatchNote, setFaceMatchNote] = useState('');
  
  // AI Guide states
  const [showAIGuide, setShowAIGuide] = useState(false);
  const [selectedGuideCustomer, setSelectedGuideCustomer] = useState(null);
  
  const { activeBranch } = useBranch();
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const faceVideoRef = useRef(null);
  const faceStreamRef = useRef(null);

  const fetchCheckInStatus = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/check-in-status', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setCurrentBatchData(response.data.current_batches || { regular: null, premium: null });
      setNextBatchData(response.data.next_batches || { regular: null, premium: null });
      setWaitingData(response.data.waiting_data || { regular: [], premium: [] });
    } catch (error) {
      console.error('Failed to fetch check-in status');
    }
  }, [activeBranch]);

  const fetchTodayCheckins = useCallback(async () => {
    try {
      const response = await api.get('/attendance/today-checkins', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setTodayCheckins(response.data.checkins || []);
      setCurrentBatch(response.data.current_batch);
    } catch (error) {
      console.error('Failed to load today check-ins');
    }
  }, [activeBranch]);

  const fetchGraceStatus = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/live-status', {
        params: { branch_id: activeBranch?.branch_id }
      });
      
      if (response.data.current_batch) {
        setGraceStatus({
          currentBatch: response.data.current_batch,
          nextBatch: response.data.next_batch
        });
      } else {
        setGraceStatus(null);
      }
    } catch (error) {
      console.error('Failed to fetch live status');
    }
  }, [activeBranch]);

  const fetchAvailableBatches = useCallback(async () => {
    try {
      // Fetch both regular and premium batches
      const [regularResponse, premiumResponse] = await Promise.all([
        api.get('/batches/schedule', { params: { branch_id: activeBranch?.branch_id } }),
        api.get('/batches/premium-schedule', { params: { branch_id: activeBranch?.branch_id } })
      ]);
      
      // Combine both batch types
      const regularBatches = (regularResponse.data.batches || []).map(b => ({
        ...b,
        batch_type: 'regular'
      }));
      
      const premiumBatches = (premiumResponse.data.batches || []).map(b => ({
        ...b,
        batch_type: 'premium'
      }));
      
      const allBatches = [...regularBatches, ...premiumBatches];
      setAvailableBatches(allBatches);
    } catch (error) {
      console.error('Failed to fetch batches');
    }
  }, [activeBranch]);

  const fetchNextBatchWaitingList = useCallback(async () => {
    try {
      const response = await api.get('/attendance/next-batch-waiting', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setNextBatchWaitingList(response.data.waiting_customers || []);
    } catch (error) {
      console.error('Failed to fetch next batch waiting list');
    }
  }, [activeBranch]);

  useEffect(() => {
    if (activeBranch) {
      fetchTodayCheckins();
      fetchGraceStatus();
      fetchCheckInStatus();
      fetchAvailableBatches();
      fetchNextBatchWaitingList();
      // Refresh every 10 seconds for real-time updates
      const interval = setInterval(() => {
        fetchTodayCheckins();
        fetchGraceStatus();
        fetchCheckInStatus();
        fetchAvailableBatches();
        fetchNextBatchWaitingList();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [activeBranch, fetchTodayCheckins, fetchGraceStatus, fetchCheckInStatus, fetchAvailableBatches, fetchNextBatchWaitingList]);

  const searchCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.get('/customers', {
        params: { branch_id: activeBranch?.branch_id }
      });
      const found = searchType === 'phone' 
        ? response.data.find(c => c.phone_number === searchValue)
        : response.data.find(c => c.reg_no === searchValue);
      
      if (found) {
        setCustomer(found);
      } else {
        toast.error(`Customer not found with this ${searchType === 'phone' ? 'phone number' : 'registration number'}`);
        setCustomer(null);
      }
    } catch (error) {
      console.error('Search error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to search customer';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to search customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (checkingIn) return;
    setCheckingIn(true);
    try {
      const payload = searchType === 'phone' 
        ? { phone_number: searchValue }
        : { reg_no: searchValue };
      
      // Add selected batch if manually selected
      if (selectedBatchId) {
        payload.preferred_batch_id = selectedBatchId;
      }
      
      const response = await api.post('/attendance/checkin', payload, {
        params: { branch_id: activeBranch?.branch_id }
      });
      
      const tokenData = response.data;
      setLastToken(tokenData);
      
      const bedInfo = tokenData.bed_number ? ` | Bed #${tokenData.bed_number}` : '';
      toast.success(
        `Token #${tokenData.token_number} - ${customer.name} checked in! Batch: ${tokenData.allocated_batch_time}${bedInfo}`,
        { duration: 6000 }
      );
      
      setSearchValue('');
      setCustomer(null);
      setSelectedBatchId(null); // Reset batch selection after check-in
      setSelectedBatchType(null); // Reset batch type
      setShowBatchSelector(false);
      fetchTodayCheckins();
      fetchCheckInStatus(); // Refresh combined status
      fetchAvailableBatches();
    } catch (error) {
      console.error('Check-in error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Check-in failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async (attendanceId) => {
    try {
      await api.post(`/attendance/${attendanceId}/checkout`);
      toast.success('Checked out successfully');
      fetchTodayCheckins();
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Check-out failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Check-out failed');
    }
  };

  const startQRScanner = async () => {
    setShowScanner(true);
    setScanning(true);
    
    try {
      // Wait for next tick to ensure DOM is ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!scannerRef.current) {
        toast.error('Scanner element not found');
        return;
      }

      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        async (decodedText) => {
          // QR code successfully scanned
          console.log('QR Code detected:', decodedText);
          
          // Stop scanning
          await stopQRScanner();
          
          // Process the scanned data
          await handleQRScan(decodedText);
        },
        (errorMessage) => {
          // Ignore scanning errors (continuous scanning)
        }
      );
    } catch (error) {
      console.error('QR Scanner error:', error);
      toast.error('Failed to start camera. Please check permissions.');
      setShowScanner(false);
      setScanning(false);
    }
  };

  const stopQRScanner = async () => {
    try {
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }
    } catch (error) {
      console.error('Error stopping scanner:', error);
    } finally {
      setShowScanner(false);
      setScanning(false);
    }
  };

  // Face ID Functions
  const startFaceCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: 'environment' }, // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      faceStreamRef.current = stream;
      setShowFaceCapture(true);
      
      // Wait for video element to render
      setTimeout(() => {
        if (faceVideoRef.current) {
          faceVideoRef.current.srcObject = stream;
          faceVideoRef.current.play().catch(err => {
            console.error('Video play error:', err);
          });
        }
      }, 100);
    } catch (error) {
      toast.error('Unable to access webcam. Please check permissions.');
      console.error('Webcam error:', error);
    }
  };

  const stopFaceCapture = () => {
    if (faceStreamRef.current) {
      faceStreamRef.current.getTracks().forEach(track => track.stop());
      faceStreamRef.current = null;
    }
    setShowFaceCapture(false);
  };

  const captureFacePhoto = async () => {
    if (faceVideoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = faceVideoRef.current.videoWidth;
      canvas.height = faceVideoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(faceVideoRef.current, 0, 0);
      
      // Convert canvas to blob and upload
      canvas.toBlob(async (blob) => {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('file', blob, 'face-capture.jpg');
          
          const response = await api.post('/storage/upload/face', formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          setCapturedPhoto(imgUrl(response.data.storage_path));
          stopFaceCapture();
          
          // Fetch all customers and show selection
          const customersResponse = await api.get('/customers', {
            params: { branch_id: activeBranch?.branch_id }
          });
          setAllCustomers(customersResponse.data);
          setFaceMatches([]);
          setFaceMatchNote('');
          setShowCustomerSelection(true);
          toast.success('Photo captured! Select customer from the list.');
          runFaceMatch(canvas, customersResponse.data);
        } catch (error) {
          console.error('Face photo upload failed:', error);
          toast.error('Failed to upload face photo. Please try again.');
          stopFaceCapture();
        }
      }, 'image/jpeg', 0.8);
    }
  };

  const runFaceMatch = async (canvas, customers) => {
    if (!customers.some(c => c.photo)) {
      setFaceMatchNote('No customers have a profile photo yet, so face matching is unavailable.');
      return;
    }
    setFaceMatching(true);
    try {
      const { probeFound, matches } = await rankCustomersByFace(canvas, customers, 3);
      if (!probeFound) setFaceMatchNote('No face detected in the captured photo. Try again with better lighting.');
      else if (matches.length === 0) setFaceMatchNote('No close matches found. Please pick the customer from the list.');
      setFaceMatches(matches);
    } catch (err) {
      console.error('Face match failed:', err);
      setFaceMatchNote('Face matching unavailable right now.');
    } finally {
      setFaceMatching(false);
    }
  };

  const handleCustomerSelect = (selectedCustomer) => {
    setCustomer(selectedCustomer);
    setSearchValue(selectedCustomer.phone_number);
    setSearchType('phone');
    setShowCustomerSelection(false);
    setCapturedPhoto(null);
    setFaceSearchFilter('');
    toast.success(`Customer ${selectedCustomer.name} selected`);
  };

  const cancelFaceID = () => {
    stopFaceCapture();
    setShowCustomerSelection(false);
    setCapturedPhoto(null);
    setFaceSearchFilter('');
    setFaceMatches([]);
    setFaceMatchNote('');
  };

  const handleQRScan = async (qrData) => {
    if (checkingIn) return;
    setCheckingIn(true);
    try {
      // QR data format: "REG:123" or "CUSTOMER:customer_id_123" or just "5"
      let searchKey = qrData;
      let type = 'reg_no';
      
      if (qrData.startsWith('REG:')) {
        searchKey = qrData.replace('REG:', '');
        type = 'reg_no';
      } else if (qrData.startsWith('CUSTOMER:')) {
        searchKey = qrData.replace('CUSTOMER:', '');
        type = 'customer_id';
      }
      
      // Search for customer in current branch
      const response = await api.get(`/customers?branch_id=${activeBranch.branch_id}`);
      const found = type === 'customer_id'
        ? response.data.find(c => c.customer_id === searchKey)
        : response.data.find(c => c.reg_no === searchKey);
      
      if (found) {
        setCustomer(found);
        setSearchValue(found.reg_no);
        setSearchType('reg_no');
        
        // Auto check-in with selected batch support
        try {
          const payload = { reg_no: found.reg_no };
          
          // Add selected batch if manually selected
          if (selectedBatchId) {
            payload.preferred_batch_id = selectedBatchId;
          }
          
          await api.post('/attendance/checkin', payload, {
            params: { branch_id: activeBranch?.branch_id }
          });
          
          const batchInfo = selectedBatchId ? ' to selected batch' : '';
          toast.success(`${found.name} checked in successfully via QR${batchInfo}!`);
          fetchTodayCheckins();
          fetchCheckInStatus();
          fetchAvailableBatches();
          setCustomer(null);
          setSearchValue('');
          setSelectedBatchId(null); // Reset batch selection after check-in
          setSelectedBatchType(null);
        } catch (error) {
          toast.error(getErrorMessage(error, 'Check-in failed'));
        }
      } else {
        toast.error('Customer not found with scanned QR code');
      }
    } catch (error) {
      console.error('QR scan error:', error);
      toast.error('Failed to process QR code');
    } finally {
      setCheckingIn(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(err => console.error(err));
      }
    };
  }, []);

  const getCategoryColor = (category) => {
    const colors = {
      'New Customer': 'bg-blue-100 text-blue-800',
      '10 Day Customer': 'bg-green-100 text-green-800',
      '30 Days Customer': 'bg-yellow-100 text-yellow-800',
      '60 Days Customer': 'bg-orange-100 text-orange-800',
      '90 Days Customer': 'bg-red-100 text-red-800',
      '120+ Days Customer': 'bg-purple-100 text-purple-800',
      'Buyers': 'bg-emerald-100 text-emerald-800',
      'Challenge Therapy': 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout>
      <div data-testid="checkin-page">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">Quick Check-In</h1>
          <p className="text-sm sm:text-base text-[#1C1C1A]/60">Search by registration number, phone number, or scan barcode</p>
        </div>

        {/* Current Active Batch Widget - Combined Regular + Premium */}
        {(currentBatchData.regular || currentBatchData.premium) && (
          <Card className={`p-6 mb-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-300`}>
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full bg-green-500 animate-pulse`}></div>
                <h3 className="font-semibold text-lg text-[#2C4C3B]">
                  Current Active Batches
                </h3>
              </div>
              <p className="text-sm text-[#1C1C1A]/60 mt-1">Both Regular and Premium batches</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Regular Batch (15 beds) */}
              {currentBatchData.regular && (
                <div className="bg-white p-4 rounded-lg border-2 border-blue-300">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-blue-600 text-white">Regular Schedule</Badge>
                    <span className="text-xs text-[#1C1C1A]/60">{currentBatchData.regular.capacity} beds</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-[#1C1C1A]/60 uppercase tracking-wide mb-1">Batch Time</p>
                      <p className="text-xl font-bold text-blue-600">
                        {currentBatchData.regular.start_time} - {currentBatchData.regular.end_time}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-[#1C1C1A]/60 uppercase tracking-wide mb-1">Bed Occupancy</p>
                      <p className="text-2xl font-bold text-green-600">
                        {currentBatchData.regular.occupied}/{currentBatchData.regular.capacity}
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mt-2">
                        <div
                          className="h-full bg-green-500 transition-all duration-500"
                          style={{ width: `${currentBatchData.regular.occupancy_percent}%` }}
                        />
                      </div>
                      <p className="text-xs text-[#1C1C1A]/60 mt-1">
                        {currentBatchData.regular.occupancy_percent}% occupied
                      </p>
                    </div>
                    
                    <Button
                      onClick={() => {
                        fetchAvailableBatches(); // Refresh batches before showing selector
                        setBatchSelectorType('regular');
                        setShowBatchSelector(true);
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-3"
                      size="sm"
                    >
                      📅 Select Different Batch
                    </Button>
                    {selectedBatchType === 'regular' && selectedBatchId && (
                      <p className="text-xs text-center text-blue-600 mt-2">
                        ✓ Custom batch selected
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Premium Batch (5 beds) */}
              {currentBatchData.premium && (
                <div className="bg-white p-4 rounded-lg border-2 border-amber-400">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-amber-600 text-white">Premium Schedule</Badge>
                    <span className="text-xs text-[#1C1C1A]/60">{currentBatchData.premium.capacity} beds</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-[#1C1C1A]/60 uppercase tracking-wide mb-1">Batch Time</p>
                      <p className="text-xl font-bold text-amber-600">
                        {currentBatchData.premium.start_time} - {currentBatchData.premium.end_time}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-[#1C1C1A]/60 uppercase tracking-wide mb-1">Bed Occupancy</p>
                      <p className="text-2xl font-bold text-green-600">
                        {currentBatchData.premium.occupied}/{currentBatchData.premium.capacity}
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mt-2">
                        <div
                          className="h-full bg-amber-500 transition-all duration-500"
                          style={{ width: `${currentBatchData.premium.occupancy_percent}%` }}
                        />
                      </div>
                      <p className="text-xs text-[#1C1C1A]/60 mt-1">
                        {currentBatchData.premium.occupancy_percent}% occupied
                      </p>
                    </div>
                    
                    <Button
                      onClick={() => {
                        fetchAvailableBatches(); // Refresh batches before showing selector
                        setBatchSelectorType('premium');
                        setShowBatchSelector(true);
                      }}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white mt-3"
                      size="sm"
                    >
                      ⭐ Select Different Batch
                    </Button>
                    {selectedBatchType === 'premium' && selectedBatchId && (
                      <p className="text-xs text-center text-amber-600 mt-2">
                        ✓ Custom batch selected
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Batch Selector Modal */}
        {showBatchSelector && (
          <Card className="p-6 mb-6 border-2 border-blue-500 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C4C3B]">
                Select {batchSelectorType === 'premium' ? 'Premium' : 'Regular'} Batch
              </h3>
              <Button 
                onClick={() => {
                  setShowBatchSelector(false);
                  setBatchSelectorType(null);
                }}
                variant="ghost"
                size="sm"
              >
                <X size={20} />
              </Button>
            </div>
            
            <div className="mb-4">
              <Badge className={batchSelectorType === 'premium' ? 'bg-amber-600 text-white' : 'bg-blue-600 text-white'}>
                {batchSelectorType === 'premium' ? 'Premium Schedule' : 'Regular Schedule'}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {availableBatches.filter(batch => batch.batch_type === batchSelectorType).length === 0 ? (
                <div className="col-span-full text-center py-8 text-[#1C1C1A]/60">
                  <p className="text-lg mb-2">No {batchSelectorType === 'premium' ? 'Premium' : 'Regular'} batches available</p>
                  <p className="text-sm">Batches may not have been generated yet for today.</p>
                </div>
              ) : (
                availableBatches
                  .filter(batch => batch.batch_type === batchSelectorType)
                  .map((batch) => (
                    <Button
                      key={batch.batch_id}
                      onClick={() => {
                        setSelectedBatchId(batch.batch_id);
                        setSelectedBatchType(batchSelectorType);
                        setShowBatchSelector(false);
                        toast.success(`${batchSelectorType === 'premium' ? 'Premium' : 'Regular'} batch ${batch.start_time} selected`);
                      }}
                      variant={selectedBatchId === batch.batch_id && selectedBatchType === batchSelectorType ? 'default' : 'outline'}
                      className={`h-auto py-4 flex flex-col items-center transition-all ${
                        selectedBatchId === batch.batch_id && selectedBatchType === batchSelectorType
                          ? batchSelectorType === 'premium'
                            ? 'bg-amber-600 text-white shadow-lg scale-105'
                            : 'bg-blue-600 text-white shadow-lg scale-105'
                          : 'hover:bg-blue-50 hover:border-blue-400'
                      }`}
                    >
                      <span className="text-lg font-bold">{batch.start_time}</span>
                      <span className="text-sm mt-1">
                        {batch.occupied}/{batch.capacity || 0} beds
                      </span>
                      {batch.is_current && (
                        <span className="text-xs mt-1 px-2 py-1 bg-green-500 text-white rounded-full">
                          Active
                        </span>
                      )}
                    </Button>
                  ))
              )}
            </div>
            
            {selectedBatchId && selectedBatchType === batchSelectorType && (
              <Button
                onClick={() => {
                  setSelectedBatchId(null);
                  setSelectedBatchType(null);
                  toast.info('Reset to automatic batch assignment');
                }}
                variant="outline"
                className="mt-4 w-full"
              >
                ← Reset to Automatic
              </Button>
            )}
          </Card>
        )}

        {/* Waiting for Next Batch - Combined Regular + Premium */}
        {(nextBatchData.regular || nextBatchData.premium) && (
          <Card className="p-6 mb-6 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300">
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <Clock size={28} className="text-orange-600" weight="fill" />
                <h3 className="text-xl font-semibold text-[#2C4C3B]">
                  Waiting for Next Batch
                </h3>
              </div>
              <p className="text-sm text-[#1C1C1A]/60 mt-1">
                Customers allocated to upcoming batches
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Regular Batch Waiting List */}
              {nextBatchData.regular && (
                <div className="bg-white p-4 rounded-lg border-2 border-blue-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-600 text-white">Regular - Next Batch</Badge>
                      <span className="text-xs text-[#1C1C1A]/60">15 beds</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {waitingData.regular.length}
                      </div>
                      <div className="text-xs text-[#1C1C1A]/60">Waiting</div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-[#1C1C1A]/60">Next Batch Time:</p>
                    <p className="text-lg font-bold text-blue-600">
                      {nextBatchData.regular.start_time} - {nextBatchData.regular.end_time}
                    </p>
                  </div>

                  {waitingData.regular.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {waitingData.regular.map((customer, idx) => (
                        <div 
                          key={idx} 
                          className="bg-blue-50 p-3 rounded border border-blue-200"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-[#2C4C3B] text-sm">
                              {customer.customer_name}
                            </span>
                            <Badge className="bg-blue-600 text-white text-xs">
                              #{customer.token_number}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#1C1C1A]/60">
                            <Bed size={14} />
                            <span>Bed {customer.bed_number}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#1C1C1A]/60 text-center py-4">
                      No customers waiting
                    </p>
                  )}
                </div>
              )}

              {/* Premium Batch Waiting List */}
              {nextBatchData.premium && (
                <div className="bg-white p-4 rounded-lg border-2 border-amber-400">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-600 text-white">Premium - Next Batch</Badge>
                      <span className="text-xs text-[#1C1C1A]/60">5 beds</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-amber-600">
                        {waitingData.premium.length}
                      </div>
                      <div className="text-xs text-[#1C1C1A]/60">Waiting</div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-[#1C1C1A]/60">Next Batch Time:</p>
                    <p className="text-lg font-bold text-amber-600">
                      {nextBatchData.premium.start_time} - {nextBatchData.premium.end_time}
                    </p>
                  </div>

                  {waitingData.premium.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {waitingData.premium.map((customer, idx) => (
                        <div 
                          key={idx} 
                          className="bg-amber-50 p-3 rounded border border-amber-200"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-[#2C4C3B] text-sm">
                              {customer.customer_name}
                            </span>
                            <Badge className="bg-amber-600 text-white text-xs">
                              {customer.token_number}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#1C1C1A]/60">
                            <Bed size={14} />
                            <span>Bed {customer.bed_number}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#1C1C1A]/60 text-center py-4">
                      No customers waiting
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Batch Selector */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card className="p-4 sm:p-6 md:p-8 bg-white border border-[#E5E5DF] rounded-lg">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-[#2C4C3B] mb-4 sm:mb-6">Customer Search</h2>
            
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] mb-2">
                Search By
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {setSearchType('reg_no'); setSearchValue(''); setCustomer(null);}}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${searchType === 'reg_no' ? 'bg-[#2C4C3B] text-white' : 'bg-[#E2E8E4] text-[#1C1C1A]'}`}
                  data-testid="search-type-reg"
                >
                  Registration No.
                </button>
                <button
                  onClick={() => {setSearchType('phone'); setSearchValue(''); setCustomer(null);}}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${searchType === 'phone' ? 'bg-[#2C4C3B] text-white' : 'bg-[#E2E8E4] text-[#1C1C1A]'}`}
                  data-testid="search-type-phone"
                >
                  Phone Number
                </button>
                <button
                  onClick={startFaceCapture}
                  className="px-3 py-2 rounded-md transition-colors bg-purple-600 hover:bg-purple-700 text-white text-sm flex items-center justify-center gap-2"
                  data-testid="search-type-face"
                >
                  <Camera size={18} />
                  Face ID
                </button>
                <button
                  onClick={startQRScanner}
                  disabled={scanning}
                  className="px-3 py-2 rounded-md transition-colors bg-[#C05C42] hover:bg-[#C05C42]/90 text-white text-sm flex items-center justify-center gap-2"
                  data-testid="search-type-qr"
                >
                  <QrCode size={18} />
                  QR Code
                </button>
              </div>
            </div>

            {(searchType === 'phone' || searchType === 'reg_no') && (
              <form onSubmit={searchCustomer} className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] mb-2">
                    {searchType === 'reg_no' ? 'Registration Number' : 'Phone Number'}
                  </label>
                  <Input
                    data-testid="search-input"
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={searchType === 'reg_no' ? 'Enter REG number' : 'Enter phone number'}
                    required
                    className="text-lg"
                  />
                </div>
                <Button
                  data-testid="search-customer-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#2C4C3B] hover:bg-[#2C4C3B]/90 text-white rounded-md flex items-center justify-center gap-2"
                >
                  <MagnifyingGlass size={20} />
                  {loading ? 'Searching...' : 'Search Customer'}
                </Button>
              </form>
            )}

            {customer && (
              <div className="mt-6 p-6 bg-[#E2E8E4] rounded-lg" data-testid="customer-info-card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold tracking-tight text-[#2C4C3B]">{customer.name}</h3>
                    <p className="text-sm text-[#1C1C1A]/60">{customer.phone_number}</p>
                    <p className="text-xs text-[#1C1C1A]/60 mt-1">REG: {customer.reg_no}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-[#2C4C3B]">
                      {customer.status}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(customer.customer_category)}`}>
                      {customer.customer_category}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1C1C1A]/70">Sessions Completed:</span>
                    <span className="font-semibold text-[#2C4C3B]">{customer.sessions_completed}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1C1C1A]/70">Planned Sessions:</span>
                    <span className="font-semibold text-[#2C4C3B]">{customer.sessions_planned}</span>
                  </div>
                </div>
                <Button
                  data-testid="checkin-confirm-button"
                  onClick={handleCheckIn}
                  disabled={checkingIn}
                  className="w-full bg-[#C05C42] hover:bg-[#C05C42]/90 text-white rounded-md flex items-center justify-center gap-2"
                >
                  <CheckCircle size={20} />
                  {checkingIn ? 'Checking In…' : 'Check In'}
                </Button>
              </div>
            )}
          </Card>

          <Card className="p-4 sm:p-6 md:p-8 bg-white border border-[#E5E5DF] rounded-lg">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-[#2C4C3B]">Today&apos;s Check-ins</h2>
              <div className="text-2xl sm:text-3xl font-bold text-[#2C4C3B]">{todayCheckins.length}</div>
            </div>

            {/* Current Batch Indicator */}
            {currentBatch && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-green-800 font-medium">Now Serving</div>
                    <div className="text-2xl font-bold text-green-900">Batch {currentBatch.batch_time}</div>
                  </div>
                  <div className="text-green-600">
                    <CheckCircle size={40} weight="fill" />
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-3" data-testid="today-checkins-list">
              {todayCheckins.length === 0 ? (
                <p className="text-center text-[#1C1C1A]/60 py-8">No check-ins yet today</p>
              ) : (
                todayCheckins.map((record) => (
                  <div
                    key={record.attendance_id}
                    className={`flex items-center gap-4 p-4 rounded-lg border ${
                      currentBatch && record.batch_id === currentBatch.batch_id
                        ? 'bg-green-50 border-green-300'
                        : 'bg-[#F9F9F8] border-[#E5E5DF]'
                    }`}
                  >
                    {/* Token Number */}
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#2C4C3B] flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-xs text-white/70">Token</div>
                        <div className="text-2xl font-bold text-white">{record.token_number}</div>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-[#2C4C3B]">{record.customer_name}</p>
                        {record.customer_category && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(record.customer_category)}`}>
                            {record.customer_category}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#1C1C1A]/60">
                        REG: {record.customer_reg_no}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-sm font-medium text-[#2C4C3B]">
                          Batch: {record.allocated_batch_time || 'Waiting'}
                        </p>
                        {record.bed_number && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-blue-100 text-blue-700">
                            🛏️ Bed #{record.bed_number}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#1C1C1A]/50">
                        {new Date(record.checked_in_at).toLocaleTimeString()}
                      </p>
                    </div>

                    {/* Status and Actions */}
                    <div className="flex flex-col items-end gap-2">
                      {record.checked_out_at ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600">
                          Completed
                        </span>
                      ) : currentBatch && record.batch_id === currentBatch.batch_id ? (
                        <>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-700 font-medium">
                            In Session
                          </span>
                          <Button
                            onClick={() => handleCheckOut(record.attendance_id)}
                            variant="outline"
                            size="sm"
                            className="border-[#C05C42] text-[#C05C42] hover:bg-[#C05C42] hover:text-white"
                            data-testid={`checkout-button-${record.attendance_id}`}
                          >
                            Check Out
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedGuideCustomer({ id: record.customer_id, name: record.customer_name });
                              setShowAIGuide(true);
                            }}
                            className="bg-teal-500 hover:bg-teal-600 text-white"
                          >
                            <Robot size={16} className="mr-1" weight="fill" />
                            AI Guide
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-700">
                            Waiting
                          </span>
                          <Button
                            onClick={() => handleCheckOut(record.attendance_id)}
                            variant="outline"
                            size="sm"
                            className="border-[#C05C42] text-[#C05C42] hover:bg-[#C05C42] hover:text-white"
                            data-testid={`checkout-button-${record.attendance_id}`}
                          >
                            Check Out
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedGuideCustomer({ id: record.customer_id, name: record.customer_name });
                              setShowAIGuide(true);
                            }}
                            className="bg-teal-500 hover:bg-teal-600 text-white"
                          >
                            <Robot size={16} className="mr-1" weight="fill" />
                            AI Guide
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
        
        {/* QR Scanner Modal */}
        {showScanner && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="qr-scanner-modal">
            <div className="relative bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <button
                onClick={stopQRScanner}
                className="absolute top-4 right-4 text-[#1C1C1A] hover:text-[#C05C42]"
                data-testid="close-scanner-button"
              >
                <X size={24} />
              </button>
              
              <h2 className="text-2xl font-semibold text-[#2C4C3B] mb-4">Scan QR Code</h2>
              <p className="text-sm text-[#1C1C1A]/60 mb-4">
                Point your camera at the customer&apos;s QR code to check them in
              </p>
              
              <div 
                id="qr-reader" 
                ref={scannerRef}
                className="w-full rounded-lg overflow-hidden border-2 border-[#2C4C3B]"
              ></div>
              
              <p className="text-xs text-[#1C1C1A]/50 mt-4 text-center">
                Scanning will automatically check in the customer
              </p>
            </div>
          </div>
        )}

        {/* Face ID Capture Modal */}
        {showFaceCapture && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={cancelFaceID}>
            <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-[#2C4C3B]">Capture Face Photo</h3>
                <button onClick={cancelFaceID} className="text-[#1C1C1A] hover:text-[#C05C42]">
                  <X size={24} />
                </button>
              </div>
              <video 
                ref={faceVideoRef} 
                autoPlay 
                playsInline 
                muted
                className="w-full max-h-[60vh] object-contain rounded-lg border-2 border-purple-600 bg-black"
              />
              <div className="flex gap-3 mt-4">
                <Button
                  onClick={captureFacePhoto}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Camera size={20} className="mr-2" />
                  Capture Photo
                </Button>
                <Button
                  onClick={cancelFaceID}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Customer Selection Modal (after face capture) */}
        {showCustomerSelection && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg w-full max-w-6xl my-8" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white p-6 border-b z-10 rounded-t-lg">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-[#2C4C3B]">Select Customer</h3>
                    <p className="text-sm text-[#1C1C1A]/60 mt-1">
                      Find and select the customer from the list below
                    </p>
                  </div>
                  <button onClick={cancelFaceID} className="text-[#1C1C1A] hover:text-[#C05C42]">
                    <X size={24} />
                  </button>
                </div>
                
                {/* Captured photo preview */}
                {capturedPhoto && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-wide font-semibold text-[#1C1C1A] mb-2">
                      Captured Photo:
                    </p>
                    <img 
                      src={capturedPhoto} 
                      alt="Captured" 
                      className="w-32 h-32 rounded-lg object-cover border-2 border-purple-600"
                    />
                  </div>
                )}

                {/* Search filter */}
                <div>
                  <Input
                    type="text"
                    placeholder="Filter by name, phone, or reg no..."
                    value={faceSearchFilter}
                    onChange={(e) => setFaceSearchFilter(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {!faceSearchFilter && (faceMatching || faceMatches.length > 0 || faceMatchNote) && (
                  <div data-testid="face-match-section" className="mb-6 rounded-xl border-2 border-purple-200 bg-purple-50/60 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs uppercase tracking-wide font-semibold text-purple-800">Likely matches</span>
                      {faceMatching && <span className="text-xs text-purple-700 animate-pulse">Comparing faces…</span>}
                    </div>
                    {faceMatches.length > 0 ? (
                      <div className="grid grid-cols-3 gap-4">
                        {faceMatches.map((m, idx) => {
                          const cust = allCustomers.find(c => c.customer_id === m.customer_id);
                          if (!cust) return null;
                          return (
                            <div
                              key={cust.customer_id}
                              data-testid={`face-match-${idx}`}
                              onClick={() => handleCustomerSelect(cust)}
                              className="relative border-2 border-purple-400 bg-white rounded-lg p-3 hover:bg-purple-100 cursor-pointer transition-colors"
                            >
                              <span className="absolute top-2 right-2 text-[10px] font-bold bg-purple-600 text-white px-2 py-0.5 rounded-full">{m.score}% match</span>
                              <img src={imgUrl(cust.photo)} alt={cust.name} className="w-full aspect-square object-cover rounded-lg mb-2" />
                              <p className="font-semibold text-[#2C4C3B] text-sm text-center">{cust.name}</p>
                              <p className="text-xs text-[#1C1C1A]/60 text-center">REG: {cust.reg_no} · {cust.phone_number}</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (!faceMatching && <p className="text-xs text-purple-800">{faceMatchNote}</p>)}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {allCustomers
                    .filter(cust => {
                      if (!faceSearchFilter) return true;
                      const search = faceSearchFilter.toLowerCase();
                      return (
                        cust.name.toLowerCase().includes(search) ||
                        cust.phone_number.includes(search) ||
                        cust.reg_no.toString().includes(search)
                      );
                    })
                    .map((cust) => (
                      <div
                        key={cust.customer_id}
                        onClick={() => handleCustomerSelect(cust)}
                        className="border-2 border-[#E5E5DF] rounded-lg p-4 hover:border-purple-600 hover:bg-purple-50 cursor-pointer transition-all"
                      >
                        {cust.photo ? (
                          <img
                            src={imgUrl(cust.photo)}
                            alt={cust.name}
                            className="w-full aspect-square object-cover rounded-lg mb-3"
                          />
                        ) : (
                          <div className="w-full aspect-square bg-[#E2E8E4] rounded-lg mb-3 flex items-center justify-center">
                            <span className="text-4xl font-bold text-[#2C4C3B]">
                              {cust.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="text-center">
                          <p className="font-semibold text-[#2C4C3B] text-sm mb-1">
                            {cust.name}
                          </p>
                          <p className="text-xs text-[#1C1C1A]/60">
                            REG: {cust.reg_no}
                          </p>
                          <p className="text-xs text-[#1C1C1A]/60">
                            {cust.phone_number}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>

                {allCustomers.filter(cust => {
                  if (!faceSearchFilter) return true;
                  const search = faceSearchFilter.toLowerCase();
                  return (
                    cust.name.toLowerCase().includes(search) ||
                    cust.phone_number.includes(search) ||
                    cust.reg_no.toString().includes(search)
                  );
                }).length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-[#1C1C1A]/60">No customers found matching your search</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AI Guide Drawer */}
      <AIGuideDrawer
        isOpen={showAIGuide}
        onClose={() => {
          setShowAIGuide(false);
          setSelectedGuideCustomer(null);
        }}
        customerId={selectedGuideCustomer?.id}
        customerName={selectedGuideCustomer?.name}
      />
    </Layout>
  );
};

export default CheckIn;